import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { Message } from '@flos/react-ui';
import ContactUsLink from './ContactUsLink';
import { useHippoText } from '../../../hooks/useHippoText';
import CallMeButton from '../../../shared_components/CallMeButton/CallMeButton';

const EDIValidationErrorMessage = ({
    coverable,
    isStartDate = false
}: {
    coverable: CoverableDTO;
    isStartDate?: boolean;
}): React.JSX.Element => {
    interface ErrorMessage {
        errorType?: React.ComponentProps<typeof Message>['theme'];
        element: React.JSX.Element;
    }
    const getHippoText = useHippoText();
    const errorMessageMap: Record<string, ErrorMessage> = {
        Katteforsikring: {
            element: (
                <>
                    Ud fra den valgte dato, du har valgt, kan vi se, at din kat vil være fyldt 7 år,
                    når forsikringen starter. Du kan derfor ikke købe katteforsikringen online, men{' '}
                    {<ContactUsLink>kontakt os</ContactUsLink>} og hør, om der er en anden måde, vi
                    kan hjælpe dig.
                </>
            )
        },
        Hundeforsikring: {
            element: (
                <>
                    Ud fra den valgte dato, du har valgt, kan vi se, at din hund vil være fyldt 7
                    år, når forsikringen starter. Du kan derfor ikke købe hundeforsikringen online,
                    men {<ContactUsLink>kontakt os</ContactUsLink>} og hør, om der er en anden måde,
                    vi kan hjælpe dig.
                </>
            )
        },
        Børneforsikring: {
            element: (
                <>
                    {getHippoText('DSS-ACC-CHILD-EFFECTIVE-DATE-ERROR-MESSAGE')}{' '}
                    {
                        <ContactUsLink>
                            {getHippoText('DSS-EFFECTIVE-DATE-ERROR-MESSAGE-CALL')}
                        </ContactUsLink>
                    }
                    {getHippoText('DSS-ACC-CHILD-EFFECTIVE-DATE-ERROR-MESSAGE-SECOND')}
                </>
            )
        },
        'Ulykkesforsikring til unge': {
            element: (
                <>
                    {getHippoText('DSS-ACC-YOUNG-EFFECTIVE-DATE-ERROR-MESSAGE')}{' '}
                    {
                        <ContactUsLink>
                            {getHippoText('DSS-EFFECTIVE-DATE-ERROR-MESSAGE-CALL')}
                        </ContactUsLink>
                    }
                    {getHippoText('DSS-ACC-YOUNG-EFFECTIVE-DATE-ERROR-MESSAGE-SECOND')}
                </>
            )
        },
        'Indboforsikring til unge': {
            element: (
                <>
                    Ud fra den dato, du har valgt, kan vi se, at du vil være fyldt 28 år, når
                    forsikringen starter. Du kan derfor ikke købe indboforsikringen til unge.{' '}
                    {<ContactUsLink>Bestil et opkald</ContactUsLink>}, så finder vi en anden løsning
                    til dig.
                </>
            )
        },
        Bilforsikring: {
            errorType: 'warning',
            element: (
                <div className="u-display-flex u-flex-column">
                    <p>
                        {getHippoText(
                            'DSS-CHECKOUT-EFFECTIVE-DATE-AND-CANCELLATION-ERROR-MESSAGE-TEXT'
                        )}
                    </p>
                    <CallMeButton buttonText="Kontakt os" />
                </div>
            )
        },
        Ulykkesforsikring: isStartDate
            ? {
                  element: (
                      <>
                          Ud fra den dato, du har valgt, kan vi se, at du vil være fyldt 80 år, når
                          forsikringen starter. Du kan derfor ikke købe en ulykkesforsikring i
                          Topdanmark.
                      </>
                  )
              }
            : {
                  element: (
                      <>
                          Ud fra den dato, du har valgt, kan vi se, at du vil være fyldt 80 år, når
                          forsikringen starter. Du kan derfor ikke købe ulykkesforsikringen online.{' '}
                          {<ContactUsLink>Bestil et opkald</ContactUsLink>}, så ser vi, om vi kan
                          finde vi en anden løsning til dig.
                      </>
                  )
              }
    };

    return (
        <>
            <Message
                theme={errorMessageMap[coverable.flavourName ?? ''].errorType ?? 'error'}
                title={errorMessageMap[coverable.flavourName ?? ''].element}
                className="edi-validation-error-message"
            />
        </>
    );
};

export default EDIValidationErrorMessage;
